/* ============================================================
   MRK — global helpers for all mrk- landing sections.
   Sections keep their own init logic (self-contained IIFEs that
   also listen to shopify:section:load); this file only exposes
   shared utilities under window.MRK.
   ============================================================ */
(function () {
  'use strict';

  if (window.MRK) return;

  var MRK = {};

  /* Format cents into money using the shop's money format when the
     theme exposes it, otherwise a plain $x.xx fallback. */
  MRK.money = function (cents) {
    var amount = (cents / 100).toFixed(2);
    var format = window.MRK_MONEY_FORMAT || (window.theme && window.theme.moneyFormat);
    if (format) {
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }
    return '$' + amount;
  };

  /* Add items to cart via AJAX, then refresh + open the theme's
     cart drawer. items: [{ id, quantity, selling_plan? }] */
  MRK.addToCart = function (items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        location.href = '/checkout';
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function (result) {
        
        // return MRK.refreshCartDrawer().then(function () { return result; });
      });
  };

  /* Re-render the theme cart drawer by replacing the whole <cart-drawer>
     element with a freshly rendered one, then opening it. Replacing the
     element re-runs its connectedCallback so the theme re-binds correctly. */
  MRK.refreshCartDrawer = function () {
    return fetch('/?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections && sections['cart-drawer'];
        var live = document.querySelector('cart-drawer');
        if (html && live) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var fresh = tmp.querySelector('cart-drawer');
          if (fresh) {
            if (typeof live.replaceWith === 'function') {
              live.replaceWith(fresh);
              live = document.querySelector('cart-drawer');
            } else {
              var fi = fresh.querySelector('#CartDrawer');
              var li = live.querySelector('#CartDrawer');
              if (fi && li) li.innerHTML = fi.innerHTML;
            }
            if (live) live.classList.remove('is-empty');
          }
        }
        MRK.updateCartCount();
        if (live && typeof live.open === 'function') {
          window.requestAnimationFrame(function () {
            try { live.open(); } catch (e) { /* content is present even if focus-trap fails */ }
          });
        } else {
          document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true }));
        }
      })
      .catch(function () { /* drawer refresh is best-effort */ });
  };

  /* Sync the header/cart count bubbles from the live cart. */
  MRK.updateCartCount = function () {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document
          .querySelectorAll('[data-cart-count], .cart-count, .header__cart-count, .cart-count-bubble, .cart-drawer__count-bubble__number')
          .forEach(function (el) { el.textContent = cart.item_count; });
        return cart;
      })
      .catch(function () {});
  };

  /* Estimated delivery date label, skipping weekends.
     opts: { cutoffHour: 14, processingDays: 1, transitDays: 6 } */
  MRK.deliveryDate = function (opts) {
    opts = opts || {};
    var cutoff = typeof opts.cutoffHour === 'number' ? opts.cutoffHour : 14;
    var processing = typeof opts.processingDays === 'number' ? opts.processingDays : 1;
    var transit = typeof opts.transitDays === 'number' ? opts.transitDays : 6;
    var now = new Date();
    if (now.getHours() >= cutoff) processing += 1;

    function addBizDays(date, n) {
      var r = new Date(date);
      var c = 0;
      while (c < n) {
        r.setDate(r.getDate() + 1);
        if (r.getDay() !== 0 && r.getDay() !== 6) c++;
      }
      return r;
    }

    var d = addBizDays(now, processing + transit);
    var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return days[d.getDay()] + ', ' + months[d.getMonth()] + ' ' + d.getDate();
  };

  /* Register a per-section init that also re-runs when the theme
     editor reloads a section. initFn receives the section container. */
  MRK.sectionInit = function (selector, initFn) {
    function run(root) {
      (root || document).querySelectorAll(selector).forEach(function (el) {
        if (el.dataset.mrkInit === 'true') return;
        el.dataset.mrkInit = 'true';
        initFn(el);
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { run(document); });
    } else {
      run(document);
    }
    document.addEventListener('shopify:section:load', function (e) { run(e.target); });
  };

  /* Simple scroll-snap slider controller: prev/next arrows + dots.
     root must contain .mrk-slider; arrows use [data-mrk-prev]/[data-mrk-next]. */
  MRK.slider = function (root) {
    var track = root.querySelector('.mrk-slider');
    if (!track) return;
    var prev = root.querySelector('[data-mrk-prev]');
    var next = root.querySelector('[data-mrk-next]');

    function step() {
      var slide = track.children[0];
      if (!slide) return 0;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
  };

  /* Accordion: root contains [data-mrk-acc-item] > [data-mrk-acc-trigger]
     + [data-mrk-acc-body]. Single-open behavior. */
  MRK.accordion = function (root, options) {
    options = options || {};
    var items = root.querySelectorAll('[data-mrk-acc-item]');
    items.forEach(function (item) {
      var trigger = item.querySelector('[data-mrk-acc-trigger]');
      var body = item.querySelector('[data-mrk-acc-body]');
      if (!trigger || !body) return;
      trigger.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');
        if (!options.multiple) {
          items.forEach(function (other) {
            other.classList.remove('is-open');
            var t = other.querySelector('[data-mrk-acc-trigger]');
            var b = other.querySelector('[data-mrk-acc-body]');
            if (t) t.setAttribute('aria-expanded', 'false');
            if (b) b.style.maxHeight = '0';
          });
        }
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          body.style.maxHeight = body.scrollHeight + 'px';
        } else {
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          body.style.maxHeight = '0';
        }
      });
    });
  };

  /* Popup manager: MRK.popup.open(el) / close(el). */
  MRK.popup = {
    open: function (el) {
      el.classList.add('is-open');
      document.body.classList.add('mrk-lock-scroll');
    },
    close: function (el) {
      el.classList.remove('is-open');
      document.body.classList.remove('mrk-lock-scroll');
    }
  };

  window.MRK = MRK;
})();
